import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import './Dashboard.css';
import { FaCheckCircle, FaTimesCircle, FaUndo, FaStickyNote } from 'react-icons/fa';

interface Page {
  name: string;
  description: string;
  path: string;
  icon?: string;
}

interface Section {
  title: string;
  pages: Page[];
  icon?: string;
}

interface Project {
  name: string;
  sections: Section[];
}

interface PagesJson {
  projects: Project[];
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<PagesJson | null>(null);
  const [error, setError] = useState('');
  const [noteModal, setNoteModal] = React.useState<null | { pagePath: string, note: string }>(null);

  useEffect(() => {
    fetch('/data/pages.json')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load pages.json');
        return res.json();
      })
      .then((json) => setData(json))
      .catch((err) => setError(err.message));
  }, []);

  if (error) {
    return (
      <div className="dashboard-v2">
        <div className="dashboard-error">
          <div className="error-icon">!</div>
          <h3>Error Loading Templates</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="dashboard-v2">
        <div className="dashboard-error">
          <div className="error-icon">!</div>
          <h3>No Templates Found</h3>
          <p>No template data was found. Please check your configuration.</p>
        </div>
      </div>
    );
  }

  // For now, just use the first project
  const project = data.projects[0];
  const sectionCount = project.sections?.length || 0;
  const templateCount = project.sections.reduce((acc, section) => acc + section.pages.length, 0);

  // Handler to open the modal from PageCard
  const openNoteModal = (pagePath: string, note: string) => {
    setNoteModal({ pagePath, note });
  };
  // Handler to save the note from the modal
  const saveNoteModal = (pagePath: string, note: string) => {
    localStorage.setItem(`approved:${pagePath}`, 'denied');
    localStorage.setItem(`rejectionNote:${pagePath}`, note);
    setNoteModal(null);
  };
  // Handler to close the modal
  const closeNoteModal = () => setNoteModal(null);

  // Card component for dynamic preview scaling
  const PageCard: React.FC<{
    page: Page;
    sectionIndex: number;
    pageIndex: number;
    openNoteModal: (pagePath: string, note: string) => void;
    noteModalOpen: boolean;
  }> = ({ page, sectionIndex, pageIndex, openNoteModal, noteModalOpen }) => {
    const headerRef = React.useRef<HTMLDivElement>(null);
    const [vote, setVote] = React.useState<'approved' | 'denied' | undefined>(() => {
      const stored = localStorage.getItem(`approved:${page.path}`);
      if (stored === 'approved') return 'approved';
      if (stored === 'denied') return 'denied';
      return undefined;
    });
    const [note, setNote] = React.useState(() => localStorage.getItem(`rejectionNote:${page.path}`) || '');
    const handleApprove = (e: React.MouseEvent) => {
      e.preventDefault();
      setVote('approved');
      localStorage.setItem(`approved:${page.path}`, 'approved');
      localStorage.removeItem(`rejectionNote:${page.path}`);
      setNote('');
    };
    const handleDeny = (e: React.MouseEvent) => {
      e.preventDefault();
      openNoteModal(page.path, note);
    };
    const handleRevoke = (e: React.MouseEvent) => {
      e.preventDefault();
      setVote(undefined);
      localStorage.removeItem(`approved:${page.path}`);
      localStorage.removeItem(`rejectionNote:${page.path}`);
      setNote('');
    };
    React.useEffect(() => {
      const setCardWidth = () => {
        if (headerRef.current) {
          const width = headerRef.current.offsetWidth;
          headerRef.current.style.setProperty('--card-width', `${width}`);
        }
      };
      setCardWidth();
      window.addEventListener('resize', setCardWidth);
      return () => window.removeEventListener('resize', setCardWidth);
    }, []);
    return (
      <Link
        key={page.path}
        to={`/theme/${page.path}`}
        className={`page-card-v2${noteModalOpen ? ' blurred' : ''}`}
        style={{
          animationDelay: `${(sectionIndex * 0.1) + (pageIndex * 0.05)}s`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Sticky note icon in top left if note exists */}
        {note && note.length > 0 && (
          <button
            className="sticky-note-icon-btn"
            title="View/Edit note"
            onClick={e => { e.preventDefault(); openNoteModal(page.path, note); }}
            style={{
              position: 'absolute',
              top: 10,
              left: 10,
              background: 'none',
              border: 'none',
              padding: 0,
              zIndex: 10,
              cursor: 'pointer',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaStickyNote style={{ color: '#facc15', fontSize: 28, filter: 'drop-shadow(0 2px 4px #facc1588)' }} />
          </button>
        )}
        {/* Modal removed from here, now rendered at root */}
        <div className="page-card-header-image" ref={headerRef} style={{position: 'relative'}}>
          <iframe
            src={`/theme/${page.path}/index.html`}
            title={`${page.name} preview`}
            className="page-card-iframe-preview"
            sandbox="allow-scripts allow-same-origin"
            loading="lazy"
            tabIndex={-1}
            width={1600}
            height={900}
            style={{ width: '1600px', height: '900px', position: 'absolute', top: 0, left: 0 }}
          />
          {/* Overlay for approval state */}
          {vote && (
            <>
              <div
                className={`approval-overlay ${vote}`}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: vote === 'approved'
                    ? 'rgba(34, 197, 94, 0.18)' // green
                    : 'rgba(239, 68, 68, 0.13)', // red
                  pointerEvents: 'none',
                  opacity: 1,
                  transition: 'opacity 0.3s',
                  zIndex: 2
                }}
              />
              <div
                className={`approval-label ${vote === 'approved' ? 'approved' : 'rejected'}`}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '140%',
                  transform: 'translate(-50%, -50%) rotate(-24deg)',
                  background: vote === 'approved' ? 'rgba(34,197,94,0.22)' : 'rgba(239,68,68,0.22)',
                  color: '#fff',
                  fontWeight: 900,
                  fontSize: '1.9rem',
                  letterSpacing: '0.18em',
                  textAlign: 'center',
                  padding: '0.5em 0',
                  boxShadow: vote === 'approved' ? '0 4px 24px #22c55e55' : '0 4px 24px #ef444455',
                  textShadow: '0 2px 12px rgba(0,0,0,0.18)',
                  borderRadius: '18px',
                  zIndex: 4,
                  userSelect: 'none',
                  pointerEvents: 'none',
                  border: '3px solid ' + (vote === 'approved' ? '#22c55e' : '#ef4444'),
                  opacity: 0.82,
                  backdropFilter: 'blur(1.5px)'
                }}
              >
                {vote === 'approved' ? 'APPROVED' : 'REJECTED'}
                {/* Sticky note icon if rejected and note exists */}
                {vote === 'denied' && localStorage.getItem(`rejectionNote:${page.path}`) && (
                  <FaStickyNote style={{ marginLeft: 12, color: '#facc15', fontSize: 24, verticalAlign: 'middle', filter: 'drop-shadow(0 2px 4px #facc1588)' }} title="See rejection note" />
                )}
              </div>
            </>
          )}
          {/* Voting controls */}
          <div
            className={`page-card-vote-controls${vote ? ' voted' : ''}`}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0,
              zIndex: 5,
              background: 'rgba(243,244,246,0.96)',
              borderRadius: '0 0 0 16px',
              boxShadow: '0 2px 8px #a0aec022',
              minHeight: 36,
              minWidth: 92,
              padding: '4px 4px 4px 8px',
              borderTopRightRadius: '12px',
              borderBottomLeftRadius: '12px',
              border: '1px solid #e5e7eb',
            }}
          >
            {/* Approve button (always present, disabled if approved) */}
            <button
              className={`approve-check-toggle`}
              title={vote === 'approved' ? 'Approved' : 'Mark as approved'}
              onClick={handleApprove}
              disabled={vote === 'approved'}
              style={{
                background: 'none',
                border: 'none',
                borderRadius: '6px',
                cursor: vote === 'approved' ? 'not-allowed' : 'pointer',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 4,
                marginLeft: 0,
                padding: 0,
                opacity: vote === 'approved' ? 0.35 : 1,
                transition: 'background 0.15s, opacity 0.15s',
              }}
            >
              <FaCheckCircle size={22} color={'#6b7280'} style={{ opacity: 0.6 }} />
            </button>
            {/* Reject button (always present, disabled if rejected) */}
            <button
              className={`deny-check-toggle`}
              title={vote === 'denied' ? 'Rejected' : 'Mark as rejected'}
              onClick={handleDeny}
              disabled={vote === 'denied'}
              style={{
                background: 'none',
                border: 'none',
                borderRadius: '6px',
                cursor: vote === 'denied' ? 'not-allowed' : 'pointer',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 4,
                marginLeft: 0,
                padding: 0,
                opacity: vote === 'denied' ? 0.35 : 1,
                transition: 'background 0.15s, opacity 0.15s',
              }}
            >
              <FaTimesCircle size={22} color={'#6b7280'} style={{ opacity: 0.6 }} />
            </button>
            {/* Undo button (only enabled if voted) */}
            <button
              className="revoke-vote-toggle"
              title="Revoke vote"
              onClick={handleRevoke}
              disabled={!vote}
              style={{
                background: 'none',
                border: 'none',
                borderRadius: '6px',
                cursor: vote ? 'pointer' : 'not-allowed',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 0,
                padding: 0,
                opacity: vote ? 1 : 0.35,
                transition: 'background 0.15s, opacity 0.15s',
              }}
            >
              <FaUndo size={18} color="#6b7280" style={{ opacity: 0.6 }} />
            </button>
          </div>
        </div>
        <div className="page-card-content-v2">
          <div className="page-header">
            <h3 className="page-name-v2">{page.name}</h3>
          </div>
          <p className="page-description-v2">{page.description}</p>
          <div className="page-footer">
            <span className="page-path-v2">{page.path}</span>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="dashboard-v2">
      {/* Fullscreen rejection note modal */}
      {noteModal && (
        <div className="rejection-note-modal-overlay">
          <div className="rejection-note-modal">
            <div className="rejection-note-title">What should be fixed?</div>
            <RejectionNoteSticky
              initialNote={noteModal.note}
              onSave={note => saveNoteModal(noteModal.pagePath, note)}
              onCancel={closeNoteModal}
            />
            <div className="rejection-note-actions" style={{display: 'none'}}></div>
          </div>
        </div>
      )}
      <Header 
        showStats={true}
        sectionCount={sectionCount}
        templateCount={templateCount}
        figmaUrl="https://www.figma.com/design/NlRKrNILVcieKtYUgwiiec"
        githubUrl=""
        liveUrl=""
        subtitle="Pensando no futuro, cuidando do presente"
      />

      <main className="dashboard-content container">
        {project.sections.map((section, sectionIndex) => (
          <section key={section.title} className="dashboard-section">
            <h2 className="section-title">
              {section.title}
              <span className="section-count">({section.pages.length})</span>
            </h2>
            <div className="pages-grid-v2">
              {section.pages.map((page, pageIndex) => (
                <PageCard
                  key={page.path}
                  page={page}
                  sectionIndex={sectionIndex}
                  pageIndex={pageIndex}
                  openNoteModal={openNoteModal}
                  noteModalOpen={!!noteModal && noteModal.pagePath === page.path}
                />
              ))}
            </div>
          </section>
        ))}
      </main>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <p>
            Theme files are located in the <code>theme/</code> folder
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard; 

function RejectionNoteSticky({ initialNote, onSave, onCancel }: { initialNote: string, onSave: (note: string) => void, onCancel: () => void }) {
  const [value, setValue] = React.useState(initialNote);
  return (
    <>
      <div className="rejection-note-sticky">
        <textarea
          className="rejection-note-textarea"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="Describe the issue..."
          autoFocus
        />
      </div>
      <div className="rejection-note-actions">
        <button className="rejection-note-save" onClick={() => onSave(value)}>Save</button>
        <button className="rejection-note-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </>
  );
} 